import Select from 'react-select';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';

const UOM_OPTIONS = ['ea', 'ft', 'in', 'lb', 'kg', 'lot', 'hr'];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '28px',
    borderRadius: '2px',
    borderColor: state.isFocused ? '#94a3b8' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px #94a3b8' : 'none',
    fontSize: '0.75rem',
    '&:hover': { borderColor: '#94a3b8' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '2px' }),
  clearIndicator: (base) => ({ ...base, padding: '2px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.75rem',
    padding: '4px 8px',
    backgroundColor: state.isSelected ? '#334155' : state.isFocused ? '#f1f5f9' : 'white',
    color: state.isSelected ? 'white' : '#0f172a',
    cursor: 'pointer',
  }),
  menu: (base) => ({ ...base, fontSize: '0.75rem', borderRadius: '2px', zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
};

const compactInput =
  'w-full px-1.5 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-50';

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function POLineEditor({ lines, jobs, disabled, onChange, onAdd, onRemove }) {
  const jobOptions = jobs.map((j) => ({
    value: j.id,
    label: `${j.po_number}-${j.po_line_number} · ${j.part_number || '(no part)'}`,
    job: j,
  }));

  const findJobOption = (jobId) =>
    jobId != null ? jobOptions.find((o) => o.value === jobId) || null : null;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Line Items</h3>
        <button
          onClick={onAdd}
          disabled={disabled}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-sm hover:bg-slate-100 disabled:opacity-50"
        >
          <PlusIcon className="w-3.5 h-3.5" /> Add Line
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-12">#</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Description</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-20">Qty</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-20">UOM</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-24">Unit Price</th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-24">Extended</th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-56">For Job</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400">
                  No line items yet. Click "Add Line" to start.
                </td>
              </tr>
            ) : (
              lines.map((line, idx) => {
                const ext = Number(line.quantity || 0) * Number(line.unit_price || 0);
                return (
                  <tr key={line._key} className="hover:bg-slate-50/50">
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={line.line_number ?? ''}
                        onChange={(e) => onChange(idx, { line_number: e.target.value })}
                        disabled={disabled}
                        className={compactInput}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={line.description ?? ''}
                        onChange={(e) => onChange(idx, { description: e.target.value })}
                        disabled={disabled}
                        className={compactInput}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        step="0.001"
                        value={line.quantity ?? ''}
                        onChange={(e) => onChange(idx, { quantity: e.target.value })}
                        disabled={disabled}
                        className={`${compactInput} tabular-nums`}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <select
                        value={line.uom || 'ea'}
                        onChange={(e) => onChange(idx, { uom: e.target.value })}
                        disabled={disabled}
                        className={compactInput}
                      >
                        {UOM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        step="0.0001"
                        value={line.unit_price ?? ''}
                        onChange={(e) => onChange(idx, { unit_price: e.target.value })}
                        disabled={disabled}
                        className={`${compactInput} tabular-nums`}
                      />
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-700 tabular-nums">{fmtMoney(ext)}</td>
                    <td className="px-1 py-1">
                      <Select
                        value={findJobOption(line.intended_job_id)}
                        onChange={(opt) => onChange(idx, { intended_job_id: opt?.value ?? null })}
                        options={jobOptions}
                        isClearable
                        isDisabled={disabled}
                        placeholder="—"
                        styles={selectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    </td>
                    <td className="px-1 py-1 text-right">
                      <button
                        onClick={() => onRemove(idx)}
                        disabled={disabled}
                        className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30"
                        aria-label="Remove line"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
